/* eslint-disable max-len */

import moment from 'moment';
import api from '@/services/api';
import ls from '@/helpers/localStorage';
import sessionVars from '@/helpers/sessionVars';
import instantCreditService from '@/services/instant-credit';
import { cleanupMessaging } from '@/services/notifications/firebase';
import constants from '@/constants';

const TOKEN_EXPIRES_IN_MINUTES = 28;
const TOKEN_EXPIRES_DATE_FMT = 'YYYY-MM-DD HH:mm:s';

export default {
  namespaced: true,
  getters: {
    isLoggedIn: state => !!state.token,
    loginWithDisqus: state => state.disqusLogin && state.status.useDisqus,
    isUserRestricted: (state, getters) => process.env.RESTRICTED_ACCESS_ENABLED && !getters.isLoggedIn,
  },
  state: {
    userUnreadNotificationsCount: 0,
    userData: {},
    userInfo: {},
    loginIsBeingCalled: false,
    ssnInfoVerified: false,
    token: ls.get('token'),
    refreshToken: ls.get('refreshToken'),
    status: {},
    disqusLogin: ls.get('disqusLogin', true),
    marketDisplay: 'grid',
    usernameTries: 0,
    watchedUserDetails: {},
    // marketDisplay: ls.get('marketDisplay', 'grid'),

    // Two-Factor Authentication
    twoFactorRequired: false,
    twoFactorToken: null,
    twoFactorMethods: [],
    twoFactorEmailHint: '',
    twoFactorPhoneHint: '',
    twoFactorSecondsUntilExpiry: 0,
    showTwoFactorPrompt: false,
    pendingLoginPayload: null,

    // Registration flow tracking - used to ensure 2FA prompt appears after signup flow
    registrationFlowActive: false,

    // Cached 2FA enabled status (fetched at login to avoid modal race conditions)
    twoFactorEnabled: null,
    // Flag to suppress ADD_FUNDS when navigating to 2FA setup
    navigatingToTwoFactorSetup: false,
  },
  mutations: {
    login(state, user) {
      state.token = user.access_token;
      state.refreshToken = user.refresh_token;
      ls.set('token', user.access_token);
      ls.set('tokenExpires', moment(user['.expires']).format(TOKEN_EXPIRES_DATE_FMT));
      ls.set('refreshToken', user.refresh_token);
    },
    logout(state, data = null) {
      state.token = data;
      state.refreshToken = data;
      state.userData = {};
      ls.del('tokenExpires');
      ls.del('currentRoutePath');
      ls.del('token');
      ls.del('refreshToken');
      ls.del('disqusLogin');
      sessionVars.clear();
      instantCreditService.clear();
      cleanupMessaging();
    },
    clearRefreshToken(state) {
      state.refreshToken = null;
      ls.del('refreshToken');
    },
    setStatus(state, status) {
      state.status = status;
    },
    setUserData(state, data) {
      state.userData = data;
    },
    increaseUserUnreadNotificationsCount(state) {
      state.userUnreadNotificationsCount += 1;
    },
    setUserUnreadNotificationsCount(state, count) {
      state.userUnreadNotificationsCount = count;
    },
    setUserInfo(state, data) {
      state.userInfo = data;
    },
    setBareToken(state, data) {
      state.token = data;
    },
    setToken(state, data) {
      const expires = moment().add(TOKEN_EXPIRES_IN_MINUTES, 'minutes').format(TOKEN_EXPIRES_DATE_FMT);
      state.token = data;
      ls.set('token', data);
      ls.set('tokenExpires', expires);
    },
    setRefreshToken(state, data) {
      state.refreshToken = data;
      ls.set('refreshToken', data);
    },
    updateMarketDisplay(state, payload) {
      state.marketDisplay = payload;
      ls.set('marketDisplay', payload, process.env.LS_TTL_USER_MARKET_DISPLAY);
    },
    setUseDisqus(state, payload) {
      if (payload) {
        state.disqusLogin = true;
        ls.set('disqusLogin', payload, process.env.LS_TTL_USER_DISQUS_LOGIN);
      } else {
        state.disqusLogin = false;
        ls.del('disqusLogin');
      }
    },
    setUsernameTries(state, count) {
      state.usernameTries = count;
    },
    setLoginIsBeingCalled(state, value) {
      state.loginIsBeingCalled = value;
    },
    setWatchedUserDetails(state, trader) {
      state.watchedUserDetails = trader;
    },
    setSsnInfoVerified(state) {
      state.ssnInfoVerified = true;
    },

    // Two-Factor Authentication
    setTwoFactorChallenge(state, data) {
      state.twoFactorRequired = true;
      state.twoFactorToken = data.two_factor_token;
      state.twoFactorMethods = data.available_methods || [];
      state.twoFactorEmailHint = data.email_hint || '';
      state.twoFactorPhoneHint = data.phone_hint || '';
      state.twoFactorSecondsUntilExpiry = data.seconds_until_expiry || 300;
    },
    clearTwoFactorChallenge(state) {
      state.twoFactorRequired = false;
      state.twoFactorToken = null;
      state.twoFactorMethods = [];
      state.twoFactorEmailHint = '';
      state.twoFactorPhoneHint = '';
      state.twoFactorSecondsUntilExpiry = 0;
      state.pendingLoginPayload = null;
    },
    setShowTwoFactorPrompt(state, value) {
      state.showTwoFactorPrompt = value;
    },
    setPendingLoginPayload(state, payload) {
      state.pendingLoginPayload = payload;
    },

    // Registration flow
    setRegistrationFlowActive(state) {
      state.registrationFlowActive = true;
    },
    clearRegistrationFlowActive(state) {
      state.registrationFlowActive = false;
    },
    setTwoFactorEnabled(state, value) {
      state.twoFactorEnabled = value;
    },
    setNavigatingToTwoFactorSetup(state, value) {
      state.navigatingToTwoFactorSetup = value;
    },
  },
  actions: {
    loginDisqus() {
      return api.account.getDisqusLogin()
        .then(response => response.data);
    },
    logoutDisqus() {
      return api.account.getDisqusLogout()
        .then(response => response.data);
    },
    getDisqusUserAuth() {
      return api.account.getDisqusUserAuth()
        .then(response => response.data);
    },
    login({ commit, state }, payload = {}) {
      if (state.loginIsBeingCalled) return Promise.resolve({});

      commit('setLoginIsBeingCalled', true);
      commit('setPendingLoginPayload', payload);

      return api.account.postToken(payload)
        .then((response) => {
          commit('setLoginIsBeingCalled', false);
          commit('login', response.data);

          // Check if 2FA prompt should be shown for non-2FA users
          if (response.data.showTwoFactorPrompt === 'true') {
            commit('setShowTwoFactorPrompt', true);
          }

          return response.data;
        })
        .catch((error) => {
          commit('setLoginIsBeingCalled', false);

          // Check for 2FA challenge response
          if (error.response && error.response.data && error.response.data.error === 'two_factor_required') {
            try {
              const twoFactorData = JSON.parse(error.response.data.error_description);
              commit('setTwoFactorChallenge', twoFactorData);
              return { requiresTwoFactor: true, ...twoFactorData };
            } catch (parseError) {
              // If parsing fails, throw original error
              throw error;
            }
          }

          throw error;
        });
    },
    logout({ commit }, data = null) {
      return api.account.postLogout()
        .then(() => {
          commit('logout', data);
        })
        .catch(() => {
          commit('logout', data);
        });
    },
    clearAuthSession({ commit }, data = null) {
      commit('logout', data);
      return Promise.resolve();
    },
    register({ commit }, payload) {
      return api.account.postRegister(payload);
    },
    registerExternal({ commit }, payload) {
      return api.account.postRegisterExternal(payload);
    },
    getExternalLogins({ commit }, { pathname = window.location.pathname, type = constants.SOCIAL_LOGIN_OR_REGISTER }) {
      return api.account.getExternalLogins(pathname, type);
    },
    addExternal({ commit }, payload) {
      return api.account.postAddExternalLogin(payload)
        .then(response => response.data);
    },
    removeExternal({ commit }, payload) {
      return api.account.postRemoveLogin(payload)
        .then(response => response.data);
    },
    getUserInfo({ commit }) {
      return api.account.getUserInfo()
        .then((response) => {
          commit('setUserInfo', response.data);
          return response;
        });
    },
    getManageInfo() {
      return api.account.getManageInfo()
        .then(response => response.data);
    },
    getAccountDetail({ commit }) {
      return api.account.getAccountDetail()
        .then((response) => {
          commit('setUserData', response.data);
          return response.data;
        });
    },
    getVerifyID() {
      return api.account.getVerifyID()
        .then(response => response);
    },
    verifyID({ commit }, payload) {
      return api.account.postVerifyID(payload)
        .then(response => response.data);
    },
    getStatus({ commit }) {
      return api.account.getStatus()
        .then((response) => {
          commit('setStatus', response.data);
          return response.data;
        });
    },
    resetPassword({ commit }, payload) {
      return api.account.postResetPassword(payload);
    },
    forgotPassword({ commit }, payload) {
      return api.account.postForgotPassword(payload)
        .then(response => response.data);
    },
    changePassword({ commit }, payload) {
      return api.account.postChangePassword(payload);
    },
    setPassword({ commit }, payload) {
      return api.account.postSetPassword(payload);
    },
    uploadUserImage({ commit }, payload) {
      return api.account.postUploadUserImage(payload);
    },
    resetUserImage() {
      return api.account.putResetUserImage();
    },
    getCommunicationPreferences() {
      return api.account.getCommunicationPreferences()
        .then(response => response.data);
    },
    setCommunicationPreferences({ commit }, payload) {
      return api.account.postCommunicationPreferences(payload)
        .then(response => response.data);
    },
    setBareToken({ commit }, payload) {
      commit('setBareToken', payload);
    },
    setToken({ commit }, payload) {
      commit('setToken', payload);
    },
    setRefreshToken({ commit }, payload) {
      commit('setRefreshToken', payload);
    },
    getCaptchaSiteKey() {
      return api.account.getCaptchaSiteKey()
        .then(response => response.data);
    },
    setUseDisqus({ commit }, payload = true) {
      return new Promise((resolve) => {
        commit('setUseDisqus', payload);
        resolve();
      });
    },
    getIdUploadUrl() {
      return api.account.getIdUploadUrl()
        .then(response => response.data);
    },
    getTaxInfo({ commit }, { reasonCode = '' }) {
      return api.account.getTaxInfo({ reasonCode })
        .then(response => response.data);
    },
    postTaxInfo({ commit }, payload) {
      return api.account.postTaxInfo(payload)
        .then(response => response.data);
    },
    updateMarketDisplay({ commit }, payload) {
      return commit('updateMarketDisplay', payload);
    },
    clearRefreshToken({ commit }) {
      commit('clearRefreshToken');
    },
    setUserUnreadNotificationsCount({ commit }, count) {
      commit('setUserUnreadNotificationsCount', count);
    },
    increaseUserUnreadNotificationsCount({ commit }) {
      commit('increaseUserUnreadNotificationsCount');
    },
    getUserNameTries({ commit }) {
      return api.account.getChangeUsername()
        .then((response) => {
          commit('setUsernameTries', response.data);
          return response.data;
        });
    },
    changeUserName({ commit }, username) {
      return api.account.postChangeUsername(username)
        .then(response => response.data);
    },
    getDocumentUploadLink({ commit }, source) {
      return api.account.getDocumentUploadLink(source)
        .then(response => response.data);
    },
    setWatchedUserDetails({ commit }, trader) {
      commit('setWatchedUserDetails', trader);
    },
    agreeToTerms({ commit }, payload) {
      return api.account.putAgreedToTerms(payload)
        .then(response => response.data);
    },
    setSsnInfoVerified({ commit }) {
      commit('setSsnInfoVerified');
    },

    // Two-Factor Authentication actions
    twoFactorSendCode({ state }, { method }) {
      return api.account.postTwoFactorSendCode({
        twoFactorToken: state.twoFactorToken,
        method,
      }).then(response => response.data);
    },

    twoFactorVerify({ commit, state }, { method, code }) {
      return api.account.postTwoFactorVerify({
        twoFactorToken: state.twoFactorToken,
        method,
        code,
      }).then((response) => {
        commit('clearTwoFactorChallenge');
        commit('login', response.data);
        return response.data;
      });
    },

    getTwoFactorStatus() {
      return api.account.getTwoFactorStatus()
        .then(response => response.data);
    },

    twoFactorSetupEmailSendCode() {
      return api.account.postTwoFactorSetupEmailSendCode()
        .then(response => response.data);
    },

    twoFactorSetupEmailVerify({ commit }, { code }) {
      return api.account.postTwoFactorSetupEmailVerify({ code })
        .then(response => response.data);
    },

    twoFactorSetupPhoneSendCode() {
      return api.account.postTwoFactorSetupPhoneSendCode()
        .then(response => response.data);
    },

    twoFactorSetupPhoneVerify({ commit }, { code }) {
      return api.account.postTwoFactorSetupPhoneVerify({ code })
        .then(response => response.data);
    },

    getTwoFactorSetupAuthenticator() {
      return api.account.getTwoFactorSetupAuthenticator()
        .then(response => response.data);
    },

    twoFactorSetupAuthenticatorVerify({ commit }, { code, encryptedSecret }) {
      return api.account.postTwoFactorSetupAuthenticatorVerify({ code, encryptedSecret })
        .then(response => response.data);
    },

    twoFactorEnable({ commit }) {
      return api.account.postTwoFactorEnable()
        .then((response) => {
          // Update cached state so modal handlers know 2FA is now enabled
          commit('setTwoFactorEnabled', true);
          return response.data;
        });
    },

    twoFactorDisable({ commit }) {
      return api.account.postTwoFactorDisable()
        .then((response) => {
          // Update cached state so modal handlers know 2FA is now disabled
          commit('setTwoFactorEnabled', false);
          return response.data;
        });
    },

    // Phone Management actions
    getPhone() {
      return api.account.getPhone()
        .then(response => response.data);
    },

    phoneUpdate(_, { phoneNumber }) {
      return api.account.postPhoneUpdate({ phoneNumber })
        .then(response => response.data);
    },

    phoneVerify(_, { phoneNumber, code }) {
      return api.account.postPhoneVerify({ phoneNumber, code })
        .then(response => response.data);
    },

    dismissTwoFactorPrompt({ commit }) {
      commit('setShowTwoFactorPrompt', false);
    },

    clearTwoFactorChallenge({ commit }) {
      commit('clearTwoFactorChallenge');
    },

    // Registration flow actions
    startRegistrationFlow({ commit }) {
      commit('setRegistrationFlowActive');
    },
    endRegistrationFlow({ commit }) {
      commit('clearRegistrationFlowActive');
    },

    // 2FA status caching for modal flow decisions
    async checkAndCacheTwoFactorStatus({ commit }) {
      try {
        const response = await api.account.getTwoFactorStatus();
        const status = response.data;
        commit('setTwoFactorEnabled', status.is_two_factor_enabled);
        return status.is_two_factor_enabled;
      } catch (e) {
        // On error, assume 2FA not enabled (safe default, will show prompt)
        commit('setTwoFactorEnabled', false);
        return false;
      }
    },

    setNavigatingToTwoFactorSetup({ commit }, value) {
      commit('setNavigatingToTwoFactorSetup', value);
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/user.js